import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNeedsAttentionChallenges } from '../../../services/dashboard.service';
import { CreatorChallenge } from '../../../types/challenges';
import useCursorChallenges from '../../../hooks/useCursorChallenges';
import useIsMobile from '../../../hooks/useIsMobile';
import ThreeDotsLoader from '../../common/three-dots-loader/three-dots-loader';
import ChallengeRow from './ChallengeRow';
import ClaimRewardModal from './ClaimRewardModal/ClaimRewardModal';
import AllChallengesModal from './AllChallenges/AllChallengesModal';
import styles from './challengesSection.module.css';

type ChallengesSectionProps = {
  brandName: string;
  defaultCurrency: string;
  address: any;
};

export default function ChallengesSection({ brandName, defaultCurrency, address }: ChallengesSectionProps) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [claimChallenge, setClaimChallenge] = useState<CreatorChallenge | null>(null);
  const [showAllChallenges, setShowAllChallenges] = useState(false);
  const {
    challenges,
    isLoading,
    isLoadingMore,
    listRef,
    handleScroll,
    refetch: fetchChallenges,
  } = useCursorChallenges(getNeedsAttentionChallenges);

  const isEmpty = !isLoading && challenges.length === 0;

  const handleViewAll = () => {
    if (isMobile) navigate('/dashboard/challenges');
    else setShowAllChallenges(true);
  };

  return (
    <>
      <div className={styles.challengesCard}>
        <div className={styles.header}>
          <h2>
            <span className={styles.mobileOnly}>🏆 </span>Challenges
          </h2>
          {!isLoading && (
            <button type="button" className={styles.viewAll} onClick={handleViewAll}>
              View all
            </button>
          )}
        </div>

        {isLoading && (
          <div className={styles.loaderContainer}>
            <ThreeDotsLoader />
          </div>
        )}

        {isEmpty && (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>🏆</div>
            <p>{brandName} has not created any challenges yet.</p>
          </div>
        )}

        {!isLoading && !isEmpty && (
          <div className={styles.challengeList} ref={listRef} onScroll={handleScroll}>
            {challenges.map((challenge) => (
              <ChallengeRow
                key={challenge.id}
                challenge={challenge}
                defaultCurrency={defaultCurrency}
                onClaimReward={setClaimChallenge}
              />
            ))}
            {isLoadingMore && (
              <div className={styles.loadMoreContainer}>
                <ThreeDotsLoader />
              </div>
            )}
          </div>
        )}
      </div>

      {claimChallenge?.reviewId && (
        <ClaimRewardModal
          open={Boolean(claimChallenge)}
          reviewId={claimChallenge.reviewId}
          address={address}
          onClose={() => setClaimChallenge(null)}
          onClaimed={() => {
            setClaimChallenge(null);
            fetchChallenges();
          }}
        />
      )}

      <AllChallengesModal
        open={showAllChallenges}
        onClose={() => setShowAllChallenges(false)}
        defaultCurrency={defaultCurrency}
        address={address}
        brandName={brandName}
        onClaimed={fetchChallenges}
        onClaimReward={(challenge) => {
          setShowAllChallenges(false);
          setClaimChallenge(challenge);
        }}
      />
    </>
  );
}
